import React from "react";
import ReactDOM from "react-dom/client";
import { FluentProvider, webLightTheme } from "@fluentui/react-components";
import { PublicClientApplication, EventType, EventMessage, AuthenticationResult } from "@azure/msal-browser";
import { MsalProvider } from "@azure/msal-react";
import App from "./App";
import "./index.css";
import { msalConfig } from "./authConfig";

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

// Clear stale MSAL state that can block login
function clearStaleMsalState() {
  const keysToCheck = Object.keys(sessionStorage);
  for (const key of keysToCheck) {
    // Clear stale interaction state
    if (key.includes("interaction.status") || 
        key.includes("request.params") ||
        key.includes("request.origin")) {
      console.log("Clearing stale MSAL state:", key);
      sessionStorage.removeItem(key);
    }
  }
}

// Async initialization
async function initializeApp() {
  try {
    // Initialize MSAL - required before any other MSAL operations
    await msalInstance.initialize();
    
    // Handle redirect response (for returning from login)
    // This MUST be called to complete any pending interaction
    let response = null;
    try {
      response = await msalInstance.handleRedirectPromise();
    } catch (redirectError) {
      // If redirect handling fails, clear stale state and continue
      console.warn("Redirect handling failed, clearing stale state:", redirectError);
      clearStaleMsalState();
    }
    
    if (response) {
      console.log("Login completed, setting active account");
      msalInstance.setActiveAccount(response.account);
    } else {
      // Set active account if one exists
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        msalInstance.setActiveAccount(accounts[0]);
      } else {
        // No accounts and no response - clear any stale state
        // This prevents "You've logged into Azure" dead-end
        clearStaleMsalState();
      }
    }

    // Set active account on login success
    msalInstance.addEventCallback((event: EventMessage) => {
      if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
        const payload = event.payload as AuthenticationResult;
        msalInstance.setActiveAccount(payload.account);
      }
    });

    // Render app
    const root = ReactDOM.createRoot(
      document.getElementById("root") as HTMLElement
    );

    root.render(
      <React.StrictMode>
        <MsalProvider instance={msalInstance}>
          <FluentProvider theme={webLightTheme}>
            <App />
          </FluentProvider>
        </MsalProvider>
      </React.StrictMode>
    );
  } catch (error) {
    console.error("Failed to initialize MSAL:", error);
    // Show error in UI
    const root = document.getElementById("root");
    if (root) {
      root.innerHTML = `<div style="padding: 20px; color: red;">Failed to initialize authentication. Please refresh the page.</div>`;
    }
  }
}

initializeApp();
